import type { ChangeEvent, ComponentRef, HTMLAttributes, RefObject } from 'react';

import { Check } from 'lucide-react';

import { cn } from '../lib/utils';

export type CheckboxProps = HTMLAttributes<HTMLInputElement> & {
    ref?: RefObject<ComponentRef<'input'>>;
    name?: string;
    onCheckedChange?: (checked: boolean) => void;
    checked?: boolean;
    disabled?: boolean;
    icon?: React.ReactNode;
};

export function Checkbox(props: CheckboxProps) {
    const { className, disabled, ref, onCheckedChange, icon, ...rest } = props;

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        if (onCheckedChange) {
            onCheckedChange(event.target.checked);
        }
    };

    return (
        <label
            className={cn(
                'inline-flex items-center gap-2 cursor-pointer select-none',
                disabled && 'cursor-not-allowed opacity-50',
            )}
            role="checkbox"
            aria-checked={props.checked}
            aria-disabled={disabled}
            aria-label={props.name}
        >
            <input
                type="checkbox"
                ref={ref}
                onChange={handleChange}
                className="sr-only"
                {...rest}
            />
            <span
                className={cn(
                    'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-input bg-background text-primary  transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    props.checked && 'bg-primary text-primary-foreground',
                    className,
                )}

            >
                {props.checked && (icon || <Check className="h-4 w-4" />)}
            </span>
        </label>
    );
}

Checkbox.displayName = 'Checkbox';
